import {useState, useCallback, useRef, useEffect} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {
    ArrowLeft,
    Save,
} from 'lucide-react'
import {routes} from '@/lib/routes'
import {usePageTitle} from '@/hooks/use-page-title'
import {useToast} from '@/hooks/use-toast'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {MutedText} from '@/components/shared/MutedText'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {permissions} from '@/lib/permissions'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {ErrorBanner} from '@/components/ui/error-banner'
import {ScriptEditorCore} from '@/components/shared/ScriptEditorCore'
import {HandlerType} from '@/types/background-jobs'
import type {TaskDefinition} from '@/types/background-jobs'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {PageSpinner} from '@/components/shared/PageSpinner'

// ============================================================================
// Main Component
// ============================================================================

export function ScriptEditorPage() {
    const services = useServices()
    const {id: jobId, taskId} = useParams<{id: string; taskId: string}>()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const {hasPermission} = useAuth()
    const i18n = useI18n()
    const {toast} = useToast()
    const canAdmin = hasPermission(permissions.jobs.manage)

    usePageTitle(i18n.scriptEditor.pageTitle)

    // Editor state
    const [script, setScript] = useState('')
    const savedScriptRef = useRef('')
    const [isDirty, setIsDirty] = useState(false)
    const [taskName, setTaskName] = useState('')

    // Dialogs
    const discardDialog = useDialog()
    const conflictDialog = useDialog()

    // All tasks ref for save mutation
    const allTasksRef = useRef<TaskDefinition[]>([])

    // Fetch tasks
    const {data: tasks, isLoading: tasksLoading, error: tasksError} = useQuery({
        queryKey: ['job-tasks', jobId],
        queryFn: () => services.jobs.tasks(jobId!),
        enabled: !!jobId,
    })

    // Initialize editor with task data — skip if a save just completed (onSuccess handles state)
    const saveJustCompletedRef = useRef(false)
    useEffect(() => {
        if (saveJustCompletedRef.current) {
            saveJustCompletedRef.current = false
            return
        }
        if (tasks && taskId) {
            allTasksRef.current = tasks
            const task = tasks.find(t => t.id === taskId)
            if (task && task.handlerType === HandlerType.GROOVY_SCRIPT) {
                const config = task.handlerConfig || {}
                const scriptContent = (config.script as string) || ''
                const initialScript = scriptContent || (i18n.scriptEditor.newScriptPlaceholder as string)
                setScript(initialScript)
                savedScriptRef.current = initialScript
                setTaskName(task.name)
                setIsDirty(false)
            }
        }
    }, [tasks, taskId, i18n.scriptEditor.newScriptPlaceholder])

    // Permission guard
    useEffect(() => {
        if (!canAdmin) {
            navigate(routes.backgroundJobs.jobs.list())
        }
    }, [canAdmin, navigate])

    // beforeunload guard for unsaved changes
    useEffect(() => {
        function handleBeforeUnload(e: BeforeUnloadEvent) {
            if (isDirty) {
                e.preventDefault()
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [isDirty])

    // Ctrl+S / Cmd+S keyboard shortcut
    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault()
                if (isDirty && !saveMutation.isPending) {
                    handleSave()
                }
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirty])

    // ========================================================================
    // Script change handler with dirty tracking
    // ========================================================================

    function handleScriptChange(newScript: string) {
        setScript(newScript)
        setIsDirty(newScript !== savedScriptRef.current)
    }

    // ========================================================================
    // Save
    // ========================================================================

    const saveMutation = useEntityMutation({
        mutationFn: async () => {
            const taskRequests = allTasksRef.current.map(t => {
                if (t.id === taskId) {
                    return {
                        name: t.name,
                        sortOrder: t.sortOrder,
                        handlerType: t.handlerType,
                        handlerConfig: {...(t.handlerConfig || {}), script},
                        timeoutMs: t.timeoutMs,
                        retryCount: t.retryCount,
                        skipOnFailure: t.skipOnFailure,
                        category: t.category,
                    }
                }
                return {
                    name: t.name,
                    sortOrder: t.sortOrder,
                    handlerType: t.handlerType,
                    handlerConfig: t.handlerConfig,
                    timeoutMs: t.timeoutMs,
                    retryCount: t.retryCount,
                    skipOnFailure: t.skipOnFailure,
                    category: t.category,
                }
            })
            const response = await services.jobs.updateTasks(jobId!, {tasks: taskRequests})
            return response
        },
        invalidate: ['job-tasks', jobId],
        successMessage: i18n.scriptEditor.saved,
        onSuccess: (updatedTasks) => {
            savedScriptRef.current = script
            setIsDirty(false)
            saveJustCompletedRef.current = true

            if (updatedTasks && Array.isArray(updatedTasks)) {
                const currentTask = allTasksRef.current.find(t => t.id === taskId)
                if (currentTask) {
                    const newTask = updatedTasks.find(t => t.sortOrder === currentTask.sortOrder && t.handlerType === HandlerType.GROOVY_SCRIPT)
                    if (newTask && newTask.id !== taskId) {
                        navigate(routes.backgroundJobs.builder.script(jobId!, newTask.id), {replace: true})
                    }
                }
                allTasksRef.current = updatedTasks
            }
        },
        onError: (error: unknown) => {
            const err = error as {status?: number}
            if (err.status === 409) {
                conflictDialog.open()
            } else {
                toast({description: i18n.scriptEditor.networkError, variant: 'destructive'})
            }
        },
    })

    const handleSave = useCallback(() => {
        saveMutation.mutate()
    }, [saveMutation])

    // ========================================================================
    // Navigation
    // ========================================================================

    function handleBack() {
        if (isDirty) {
            discardDialog.open()
        } else {
            navigate(routes.backgroundJobs.builder.edit(jobId!), {replace: true})
        }
    }

    function handleDiscardAndBack() {
        discardDialog.close()
        navigate(routes.backgroundJobs.builder.edit(jobId!), {replace: true})
    }

    // ========================================================================
    // Derived state
    // ========================================================================

    const task = tasks?.find(t => t.id === taskId)
    const taskNotFound = tasks && (!task || task.handlerType !== HandlerType.GROOVY_SCRIPT)

    // ========================================================================
    // Render
    // ========================================================================

    if (!canAdmin) return null

    // Loading
    if (tasksLoading) {
        return (
            <div data-testid="editor-loading">
                <PageSpinner className="h-screen"/>
            </div>
        )
    }

    // Error fetching tasks
    if (tasksError) {
        return (
            <Stack gap="md" className="p-6" data-testid="editor-error">
                <ErrorBanner error={tasksError as Error}/>
                <Button variant="ghost" onClick={() => navigate(routes.backgroundJobs.builder.edit(jobId!), {replace: true})} data-testid="btn-back-error">
                    <Icon icon={ArrowLeft} className="mr-2"/>
                    {i18n.common.back}
                </Button>
            </Stack>
        )
    }

    // Task not found
    if (taskNotFound) {
        return (
            <Stack gap="md" className="p-6" data-testid="editor-task-not-found">
                <ErrorBanner error={new Error(i18n.scriptEditor.taskNotFound as string)}/>
                <Button variant="ghost" onClick={() => navigate(routes.backgroundJobs.builder.edit(jobId!), {replace: true})} data-testid="btn-back-task-not-found">
                    <Icon icon={ArrowLeft} className="mr-2"/>
                    {i18n.common.back}
                </Button>
            </Stack>
        )
    }

    return (
        <Stack gap="xs" className="flex flex-col"
            data-testid="script-editor-page">
            {/* Toolbar */}
            <InlineRow
                surface="toolbar"
                justify="between"
                data-testid="editor-toolbar"
            >
                <InlineRow gap="lg">
                    <Button variant="ghost" size="sm" onClick={handleBack} data-testid="btn-back">
                        <Icon icon={ArrowLeft} className="mr-2"/>
                        {i18n.common.back}
                    </Button>
                    <MutedText size="sm" className="font-medium">{taskName}</MutedText>
                    {isDirty && (
                        <Badge variant="outline" className="text-status-warning border-amber-300" data-testid="badge-unsaved">
                            {i18n.scriptEditor.unsavedChanges}
                        </Badge>
                    )}
                </InlineRow>
                <InlineRow>
                    <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={!isDirty || saveMutation.isPending}
                        data-testid="btn-save"
                    >
                        {saveMutation.isPending ? (
                            <>
                                <ButtonSpinner/>
                                {i18n.scriptEditor.saving}
                            </>
                        ) : (
                            <>
                                <Icon icon={Save} className="mr-2"/>
                                {i18n.scriptEditor.save}
                            </>
                        )}
                    </Button>
                </InlineRow>
            </InlineRow>

            {/* Script Editor Core (Monaco + Check Syntax + AI Assist + Error Bar) */}
            <ScriptEditorCore
                script={script}
                onScriptChange={handleScriptChange}
                className="flex-1 min-h-0"
            />

            {/* Discard Changes Dialog */}
            <AlertDialog open={discardDialog.isOpen} onOpenChange={discardDialog.bind.onOpenChange}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{i18n.scriptEditor.discardTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{i18n.scriptEditor.discardDescription}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btn-cancel-discard">{i18n.common.cancel}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDiscardAndBack}
                            className="bg-destructive text-destructive-foreground"
                            data-testid="btn-confirm-discard"
                        >
                            {i18n.jobBuilder.discard}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Conflict Dialog */}
            <AlertDialog open={conflictDialog.isOpen} onOpenChange={conflictDialog.bind.onOpenChange}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{i18n.jobBuilder.conflictTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{i18n.jobBuilder.conflictDescription}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btn-conflict-cancel">{i18n.common.cancel}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                conflictDialog.close()
                                queryClient.invalidateQueries({queryKey: ['job-tasks', jobId]})
                            }}
                            data-testid="btn-conflict-reload"
                        >
                            {i18n.jobBuilder.reload}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Stack>
    )
}
